<template>
  <main class="indicator-page">
    <header class="indicator-hero">
      <p>A-Share Market Cap / GDP</p>
      <h1>A 股总市值与 GDP 比值</h1>
      <span>沪市 + 深市 + 北交所总市值 ÷ 上一年中国 GDP</span>
    </header>

    <section class="indicator-shell">
      <div class="toolbar">
        <div>
          <strong>每日收盘后更新</strong>
          <span v-if="latest">最新交易日 {{ latest.trade_date }}</span>
        </div>
        <label>
          <span>显示范围</span>
          <select v-model.number="days" @change="loadData">
            <option :value="30">最近 30 个交易日</option>
            <option :value="90">最近 90 个交易日</option>
            <option :value="250">最近 250 个交易日</option>
            <option :value="500">最近 500 个交易日</option>
          </select>
        </label>
        <button type="button" @click="loadData" :disabled="loading">
          {{ loading ? '加载中…' : '刷新页面数据' }}
        </button>
      </div>

      <div v-if="error" class="state error">{{ error }}</div>
      <div v-else-if="loading && !latest" class="state">数据加载中…</div>

      <template v-if="latest">
        <section class="metric-grid">
          <article class="metric ratio-card" :class="'verdict-' + latest.verdict">
            <span>总市值 / 上年 GDP</span>
            <strong>{{ formatPercent(latest.ratio_pct) }}</strong>
            <em>{{ bandLabel }}</em>
          </article>
          <article class="metric">
            <span>A 股总市值</span>
            <strong>{{ formatTrillion(latest.total_mktcap_100m) }}</strong>
            <em>沪深北交易所口径</em>
          </article>
          <article class="metric">
            <span>{{ latest.gdp_year }} 年 GDP</span>
            <strong>{{ formatTrillion(latest.gdp_100m) }}</strong>
            <em>国家统计局现价口径</em>
          </article>
          <article class="metric">
            <span>统计范围</span>
            <strong>{{ latest.stock_count }} 只</strong>
            <em>采集于 {{ latest.captured_at }}</em>
          </article>
        </section>

        <section class="chart-card">
          <div class="section-heading">
            <div>
              <p>Daily Series</p>
              <h2>每日走势</h2>
            </div>
            <span>{{ history.length }} 个交易日</span>
          </div>
          <div ref="historyChart" class="history-chart" role="img" aria-label="A股总市值与上年GDP比值每日走势图"></div>
        </section>

        <section class="history-card">
          <div class="section-heading">
            <div>
              <p>Daily Records</p>
              <h2>每日数据</h2>
            </div>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>交易日</th>
                  <th>A 股总市值</th>
                  <th>上年 GDP</th>
                  <th>比值</th>
                  <th>沪市</th>
                  <th>深市</th>
                  <th>北交所</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in descendingHistory" :key="row.trade_date">
                  <td>{{ row.trade_date }}</td>
                  <td>{{ formatTrillion(row.total_mktcap_100m) }}</td>
                  <td>{{ row.gdp_year }} · {{ formatTrillion(row.gdp_100m) }}</td>
                  <td class="ratio-value">{{ formatPercent(row.ratio_pct) }}</td>
                  <td>{{ formatTrillion(row.sse_100m) }}</td>
                  <td>{{ formatTrillion(row.szse_100m) }}</td>
                  <td>
                    {{ formatTrillion(row.bse_100m) }}
                    <small v-if="row.bse_missing">本次缺失</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="method-card">
          <h2>统计口径</h2>
          <p>
            总市值按上市地统计：上交所和深交所采用各自“市场总貌”的股票总市值，
            北交所采用股票行情总市值加总；每日历史按交易所数据日期去重。
            GDP 使用上一自然年度国家统计局公布的现价国内生产总值。
          </p>
          <div class="source-links">
            <a href="https://www.sse.com.cn/market/view/" target="_blank" rel="noopener">上海证券交易所</a>
            <a href="https://www.szse.cn/market/overview/index.html" target="_blank" rel="noopener">深圳证券交易所</a>
            <a :href="gdpSourceUrl" target="_blank" rel="noopener">国家统计局 GDP 来源</a>
          </div>
          <p class="disclaimer">该指标用于观察证券化程度，不构成投资建议，也不能单独代表市场高估或低估。</p>
        </section>
      </template>
    </section>
  </main>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'a-share-market-cap-gdp',
  data: function () {
    return {
      days: 250,
      latest: null,
      history: [],
      loading: false,
      error: '',
      chart: null
    }
  },
  computed: {
    descendingHistory: function () {
      return this.history.slice().reverse()
    },
    bandLabel: function () {
      return (this.latest && this.latest.detail && this.latest.detail.band) || '市场证券化率'
    },
    gdpSourceUrl: function () {
      return (
        this.latest &&
        this.latest.detail &&
        this.latest.detail.gdp_source_url
      ) || 'https://www.stats.gov.cn/sj/tjgb/ndtjgb/qgndtjgb/'
    }
  },
  mounted: function () {
    this.loadData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) this.chart.dispose()
  },
  methods: {
    loadData: function () {
      var self = this
      self.loading = true
      self.error = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/buffett/?days=' + this.days
      this.$http.get(reqUrl).then(function (response) {
        var result = JSON.parse(response.bodyText)
        if (result.result !== 'success') {
          throw new Error(result.message || '数据加载失败')
        }
        self.latest = result.latest
        self.history = result.history || []
        self.loading = false
        self.$nextTick(self.renderChart)
      }).catch(function (error) {
        self.loading = false
        self.error = error.message || 'A 股总市值数据加载失败'
      })
    },
    renderChart: function () {
      if (!this.$refs.historyChart) return
      if (!this.chart) this.chart = echarts.init(this.$refs.historyChart)
      var dates = this.history.map(function (row) { return row.trade_date })
      var ratios = this.history.map(function (row) { return row.ratio_pct })
      var caps = this.history.map(function (row) {
        return Number((row.total_mktcap_100m / 10000).toFixed(2))
      })
      this.chart.setOption({
        animationDuration: 450,
        color: ['#1f6feb', '#d97706'],
        tooltip: {
          trigger: 'axis',
          formatter: function (items) {
            if (!items || !items.length) return ''
            var lines = [items[0].axisValue]
            items.forEach(function (item) {
              lines.push(
                item.marker + item.seriesName + '：' +
                item.value + (item.seriesIndex === 0 ? '%' : ' 万亿元')
              )
            })
            return lines.join('<br>')
          }
        },
        legend: {
          top: 4,
          data: ['总市值 / GDP', 'A 股总市值']
        },
        grid: {
          left: 20,
          right: 20,
          top: 50,
          bottom: 24,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: { hideOverlap: true }
        },
        yAxis: [
          {
            type: 'value',
            name: '比值 %',
            scale: true,
            axisLabel: { formatter: '{value}%' },
            splitLine: { lineStyle: { color: '#edf1f5' } }
          },
          {
            type: 'value',
            name: '万亿元',
            scale: true,
            axisLabel: { formatter: '{value}' },
            splitLine: { show: false }
          }
        ],
        series: [
          {
            name: '总市值 / GDP',
            type: 'line',
            data: ratios,
            smooth: 0.2,
            showSymbol: ratios.length < 40,
            symbolSize: 5,
            lineStyle: { width: 3 },
            areaStyle: { opacity: 0.08 }
          },
          {
            name: 'A 股总市值',
            type: 'line',
            yAxisIndex: 1,
            data: caps,
            smooth: 0.2,
            showSymbol: caps.length < 40,
            symbolSize: 5,
            lineStyle: { width: 2 }
          }
        ]
      }, true)
    },
    resizeChart: function () {
      if (this.chart) this.chart.resize()
    },
    formatTrillion: function (value) {
      if (value == null) return '—'
      return (Number(value) / 10000).toFixed(2) + ' 万亿元'
    },
    formatPercent: function (value) {
      if (value == null) return '—'
      return Number(value).toFixed(2) + '%'
    }
  }
}
</script>

<style scoped>
.indicator-page {
  min-height: 100vh;
  background: #f3f6fa;
  color: #172033;
}

.indicator-hero {
  border-bottom: 1px solid #e1e7ee;
  padding: 48px 20px 44px;
  background: linear-gradient(135deg, #ffffff 0%, #f1f6fc 100%);
  text-align: center;
}

.indicator-hero p,
.section-heading p {
  margin: 0 0 7px;
  color: #52709a;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.indicator-hero h1 {
  margin: 0;
  color: #101828;
  font-size: 38px;
  line-height: 1.2;
}

.indicator-hero span {
  display: block;
  margin-top: 12px;
  color: #667085;
  font-size: 16px;
}

.indicator-shell {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 28px 0 72px;
}

.toolbar,
.metric,
.chart-card,
.history-card,
.method-card {
  border: 1px solid #dce4ed;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .04);
}

.toolbar {
  display: flex;
  gap: 16px;
  align-items: end;
  justify-content: flex-end;
  padding: 16px 18px;
}

.toolbar > div {
  display: grid;
  gap: 4px;
  margin-right: auto;
}

.toolbar > div span,
.toolbar label span {
  color: #667085;
  font-size: 12px;
}

.toolbar label {
  display: grid;
  gap: 5px;
}

.toolbar select,
.toolbar button {
  min-height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 12px;
  background: #fff;
  color: #263445;
  font-size: 14px;
}

.toolbar button {
  border-color: #1f6feb;
  background: #1f6feb;
  color: #fff;
  cursor: pointer;
}

.toolbar button:disabled {
  cursor: wait;
  opacity: .6;
}

.state {
  padding: 24px 2px;
  color: #667085;
}

.state.error {
  color: #b42318;
}

.metric-grid {
  display: grid;
  grid-template-columns: 1.15fr repeat(3, 1fr);
  gap: 14px;
  margin-top: 18px;
}

.metric {
  min-height: 112px;
  padding: 18px;
}

.metric span,
.metric em {
  color: #667085;
  font-size: 12px;
  font-style: normal;
}

.metric strong {
  display: block;
  margin: 12px 0 8px;
  color: #101828;
  font-size: 24px;
}

.ratio-card strong {
  font-size: 38px;
}

.ratio-card.verdict-undervalued strong { color: #16833a; }
.ratio-card.verdict-neutral strong { color: #52606d; }
.ratio-card.verdict-overvalued strong { color: #c2413b; }

.chart-card,
.history-card,
.method-card {
  margin-top: 18px;
}

.section-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 14px;
  padding: 20px 22px 0;
}

.section-heading h2,
.method-card h2 {
  margin: 0;
  color: #101828;
  font-size: 22px;
}

.section-heading > span {
  color: #667085;
  font-size: 13px;
}

.history-chart {
  width: 100%;
  height: 420px;
}

.table-wrap {
  overflow-x: auto;
  padding: 16px 22px 22px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

th,
td {
  border-bottom: 1px solid #e8edf2;
  padding: 11px 10px;
  text-align: right;
  white-space: nowrap;
}

th {
  color: #667085;
  font-weight: 700;
}

th:first-child,
td:first-child {
  text-align: left;
}

.ratio-value {
  color: #1f5fae;
  font-weight: 700;
}

td small {
  display: block;
  color: #b42318;
}

.method-card {
  padding: 22px;
}

.method-card p {
  margin: 12px 0 0;
  color: #52606d;
  line-height: 1.75;
}

.source-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  margin-top: 14px;
}

.source-links a {
  color: #1f6feb;
}

.method-card .disclaimer {
  color: #8a5b12;
  font-size: 12px;
}

@media (max-width: 900px) {
  .indicator-shell {
    width: min(100% - 28px, 720px);
  }

  .metric-grid {
    grid-template-columns: 1fr 1fr;
  }

  .toolbar {
    align-items: stretch;
    flex-wrap: wrap;
  }

  .toolbar > div {
    flex-basis: 100%;
  }
}

@media (max-width: 560px) {
  .indicator-hero h1 {
    font-size: 30px;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .history-chart {
    height: 340px;
  }

  .toolbar label,
  .toolbar select,
  .toolbar button {
    width: 100%;
  }
}
</style>
